<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { SelectCards } from '@/modules/shared/components'
import { INVESTMENT_TYPES, INVESTMENT_SIZES, INVESTMENT_SIZE_LABELS } from '@/modules/connecting/constants/investment'

const investmentSizeOptions = INVESTMENT_SIZES.map((size) => ({
  value: size,
  label: INVESTMENT_SIZE_LABELS[size],
  description: '',
}))

const props = defineProps<{
  modelValue: {
    investment_types_interested: string[]
    fund_investment_range: string | null
    spv_investment_range: string | null
    deal_investment_range: string | null
  }
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: typeof props.modelValue): void
}>()

const form = ref({ ...props.modelValue })

watch(
  () => props.modelValue,
  (val) => {
    form.value = { ...val }
  },
  { deep: true },
)

watch(
  form,
  (val) => {
    emit('update:modelValue', { ...val })
  },
  { deep: true },
)

const isFundSelected = computed(() => form.value.investment_types_interested.includes('fund'))
const isSyndicateSelected = computed(() => form.value.investment_types_interested.includes('syndicate'))
const isAngelGroupSelected = computed(() => form.value.investment_types_interested.includes('angel_group'))
const hasTypes = computed(() => form.value.investment_types_interested.length > 0)

const rules = {
  investment_types_interested: { required },
  fund_investment_range: {
    isRequired: helpers.withMessage('Please select a fund investment amount', (value: string) =>
      isFundSelected.value ? !!value : true,
    ),
  },
  spv_investment_range: {
    isRequired: helpers.withMessage('Please select a syndicate investment amount', (value: string) =>
      isSyndicateSelected.value ? !!value : true,
    ),
  },
  deal_investment_range: {
    isRequired: helpers.withMessage('Please select a deal investment amount', (value: string) =>
      isAngelGroupSelected.value ? !!value : true,
    ),
  },
}

const v$ = useVuelidate(rules, form, { $lazy: true })

const isValid = async () => {
  return await v$.value.$validate()
}

defineExpose({ isValid })
</script>

<template>
  <div>
    <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment type</div>
    <div class="mb-6">
      <SelectCards
        v-model="form.investment_types_interested"
        :options="INVESTMENT_TYPES"
        :multiple="true"
        property="investment_types_interested"
        :v$="v$"
      />
    </div>

    <div v-if="hasTypes" class="amts-wrap">
      <template v-if="isFundSelected">
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment commitment per fund</div>
        <div class="mb-6">
          <SelectCards
            v-model="form.fund_investment_range"
            :options="investmentSizeOptions"
            property="fund_investment_range"
            :v$="v$"
            :columns="3"
          />
        </div>
      </template>

      <template v-if="isSyndicateSelected">
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
          Investment commitment per syndicate (SPV)
        </div>
        <div class="mb-6">
          <SelectCards
            v-model="form.spv_investment_range"
            :options="investmentSizeOptions"
            property="spv_investment_range"
            :v$="v$"
            :columns="3"
          />
        </div>
      </template>

      <template v-if="isAngelGroupSelected">
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment commitment per deal</div>
        <div class="mb-6">
          <SelectCards
            v-model="form.deal_investment_range"
            :options="investmentSizeOptions"
            property="deal_investment_range"
            :v$="v$"
            :columns="3"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.amts-wrap {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
